/**
 * GENERATED FILE — do not edit.
 *
 * Source: a scan of every .tsx file under src/. Regenerate with `npm run build:design-usage`.
 *
 * Deliberately NOT CI-gated: icon counts move with almost every PR, and a gate
 * that fails constantly for no defect gets bypassed — which costs you the gates
 * that matter. Same posture as CODE_CAPABILITY_MAP.md. The page states when it
 * was last measured.
 */

export interface GlyphUsage { name: string; count: number }

export interface ConceptUsage {
  concept: string;
  /** The ruling — the glyph to use in new code. */
  canonical: string;
  why: string;
  usage: GlyphUsage[];
}

export interface SizeUsage { cls: string; count: number }

export const MEASURED_ON = "2026-08-29";
export const FILES_IMPORTING_LUCIDE = 1170;
export const DISTINCT_ICONS = 371;
/** Uses of a size outside the named ramp. */
export const OFF_RAMP_SIZE_USES = 616;

export const SIZE_RAMP: SizeUsage[] = [
  {
    "cls": "h-3 w-3",
    "count": 1408
  },
  {
    "cls": "h-3.5 w-3.5",
    "count": 1957
  },
  {
    "cls": "h-4 w-4",
    "count": 4380
  },
  {
    "cls": "h-5 w-5",
    "count": 1535
  },
  {
    "cls": "h-6 w-6",
    "count": 515
  },
  {
    "cls": "h-8 w-8",
    "count": 349
  }
];

export interface ScaleStep { step: string; count: number }

/** Flex/grid gap steps. */
export const GAP_SCALE: ScaleStep[] = [
  {
    "step": "0.5",
    "count": 108
  },
  {
    "step": "1",
    "count": 3407
  },
  {
    "step": "1.5",
    "count": 1560
  },
  {
    "step": "2",
    "count": 5564
  },
  {
    "step": "2.5",
    "count": 104
  },
  {
    "step": "3",
    "count": 2460
  },
  {
    "step": "4",
    "count": 1100
  },
  {
    "step": "5",
    "count": 18
  },
  {
    "step": "6",
    "count": 304
  },
  {
    "step": "8",
    "count": 86
  }
];
/** Container padding steps. */
export const PADDING_SCALE: ScaleStep[] = [
  {
    "step": "0.5",
    "count": 100
  },
  {
    "step": "1",
    "count": 570
  },
  {
    "step": "1.5",
    "count": 219
  },
  {
    "step": "2",
    "count": 699
  },
  {
    "step": "2.5",
    "count": 51
  },
  {
    "step": "3",
    "count": 1038
  },
  {
    "step": "4",
    "count": 2197
  },
  {
    "step": "5",
    "count": 273
  },
  {
    "step": "6",
    "count": 1218
  },
  {
    "step": "8",
    "count": 389
  }
];
/** Page and container max-widths — there is currently no convention here. */
export const WIDTH_USAGE: ScaleStep[] = [
  {
    "step": "xs",
    "count": 61
  },
  {
    "step": "sm",
    "count": 93
  },
  {
    "step": "md",
    "count": 141
  },
  {
    "step": "lg",
    "count": 98
  },
  {
    "step": "xl",
    "count": 24
  },
  {
    "step": "2xl",
    "count": 147
  },
  {
    "step": "3xl",
    "count": 122
  },
  {
    "step": "4xl",
    "count": 86
  },
  {
    "step": "5xl",
    "count": 27
  },
  {
    "step": "6xl",
    "count": 36
  },
  {
    "step": "7xl",
    "count": 52
  }
];
/** Grid column counts. */
export const GRID_COLS: ScaleStep[] = [
  {
    "step": "1",
    "count": 324
  },
  {
    "step": "2",
    "count": 885
  },
  {
    "step": "3",
    "count": 335
  },
  {
    "step": "4",
    "count": 166
  },
  {
    "step": "5",
    "count": 36
  },
  {
    "step": "6",
    "count": 11
  },
  {
    "step": "7",
    "count": 4
  },
  {
    "step": "8",
    "count": 5
  }
];

export const CONCEPT_USAGE: ConceptUsage[] = [
  {
    "concept": "Edit",
    "canonical": "Pencil",
    "why": "Plainest glyph and already the plurality. Edit/Edit2/Edit3 are the same pencil at different lucide vintages.",
    "usage": [
      {
        "name": "Pencil",
        "count": 68
      },
      {
        "name": "Edit2",
        "count": 18
      },
      {
        "name": "Edit",
        "count": 12
      },
      {
        "name": "Edit3",
        "count": 12
      },
      {
        "name": "PencilLine",
        "count": 2
      }
    ]
  },
  {
    "concept": "Success / confirmed",
    "canonical": "Check",
    "why": "A bare check for inline confirmation; reserve CheckCircle2 for a standalone success state where the glyph carries the whole message.",
    "usage": [
      {
        "name": "Check",
        "count": 338
      },
      {
        "name": "CheckCircle",
        "count": 294
      },
      {
        "name": "CheckCircle2",
        "count": 272
      },
      {
        "name": "BadgeCheck",
        "count": 7
      }
    ]
  },
  {
    "concept": "Warning",
    "canonical": "AlertTriangle",
    "why": "Triangle reads as caution across cultures and is distinguishable from the information circle at 14px. AlertCircle is for information, not warning.",
    "usage": [
      {
        "name": "AlertCircle",
        "count": 302
      },
      {
        "name": "AlertTriangle",
        "count": 261
      },
      {
        "name": "AlertOctagon",
        "count": 4
      }
    ]
  },
  {
    "concept": "Delete",
    "canonical": "Trash2",
    "why": "X means dismiss or close, not destroy. Using X for delete is why \"close this panel\" and \"delete this item\" look identical.",
    "usage": [
      {
        "name": "Trash2",
        "count": 200
      },
      {
        "name": "XCircle",
        "count": 96
      },
      {
        "name": "MinusCircle",
        "count": 5
      }
    ]
  },
  {
    "concept": "Add",
    "canonical": "Plus",
    "why": "Already near-unanimous; keep it that way.",
    "usage": [
      {
        "name": "Plus",
        "count": 431
      },
      {
        "name": "PlusCircle",
        "count": 8
      }
    ]
  }
];
